import { useState } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useChannelPartnerCreate } from '@/hooks/useCrm';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { CRM_PERMISSIONS, hasPermission } from '@/lib/crmPermissions';

export default function ChannelPartnerCreatePage() {
  // ── All hooks must be called unconditionally at the top level ──────────────
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { loading, error, create } = useChannelPartnerCreate();

  const [name, setName] = useState('');
  const [commissionRate, setCommissionRate] = useState('');

  // Derive permission flag after all hooks are called.
  const canCreate = hasPermission(permissions, CRM_PERMISSIONS.CHANNEL_PARTNER_CREATE);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard mutation: reject if permission was revoked between renders.
    if (!canCreate) {
      toast.error('权限不足，无法创建渠道合作方');
      return;
    }
    if (!name.trim()) {
      toast.error('名称为必填项');
      return;
    }
    const result = await create({
      name: name.trim(),
      commissionRate: commissionRate.trim() || undefined,
    });
    if (result) {
      toast.success('渠道合作方创建成功');
      navigate(`/crm/channel-partners/${result.id}`);
    } else if (error) {
      toast.error(`创建失败：${error.message}`);
    }
  };

  // ── Permission early return — after all hooks ─────────────────────────────
  if (!canCreate) {
    return <AccessDenied resource="新建渠道合作方" />;
  }

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <h2 className="text-xl font-semibold">新建渠道合作方</h2>
      <Card className="p-6">
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="cp-name">名称 *</Label>
            <Input
              id="cp-name"
              data-testid="cp-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="请输入合作方名称"
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="cp-commission">佣金率</Label>
            <Input
              id="cp-commission"
              data-testid="cp-comm-input"
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              placeholder="可选，如 0.05"
            />
          </div>

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={loading}>
              {loading ? '提交中…' : '创建'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/crm/channel-partners')}
            >
              取消
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
